import { getActualDate } from 'src/app/helpers/get-actual-time';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import {
  ILabTests,
  IMedication,
  IPatientPersonalInfo,
  IPrescriptionDetails,
  IRequestInformation,
  ISelectedAddresses,
} from 'src/app/_interfaces/prescription/i-prescription-details';
import { PrescriptionService } from 'src/app/_services/prescription-fulfillment/prescription.service';
import { ImageModalComponent } from '../image-modal/image-modal.component';
import { LabTestsStatusModalComponent } from '../lab-tests-status-modal/lab-tests-status-modal.component';
import { MedicationRequestStatusModalComponent } from '../medication-request-status-modal/medication-request-status-modal.component';
import { UploadPrescriptionResultModalComponent } from '../upload-prescription-result-modal/upload-prescription-result-modal.component';
import { baseUrl } from './../../../../environments/environment';
import { PrescriptionRequestStatusEnum } from 'src/app/enums/prescription-request-status-enum';
import { DatePipe } from '@angular/common';
import { IAttachmentsFilesDTO } from 'src/app/_interfaces/medical-tests/i-medical-test-details';
import { UploadMultipleFilesModalComponent } from '@components/upload-multiple-files-modal/upload-multiple-files-modal.component';
import { ToastrService } from 'ngx-toastr';
import { showNotification } from 'src/app/helpers/show-toast';
import { PrescriptionFulfillmentTypeEnum } from 'src/app/enums/prescription-fulfillment-type-enum';
import { isPdfFile } from 'src/app/helpers/check-file-type';
import { FilesService } from 'src/app/_services/files/files.service';

@Component({
  selector: 'app-prescription-request-details',
  templateUrl: './prescription-request-details.component.html',
  styleUrls: ['./prescription-request-details.component.scss'],
})
export class PrescriptionRequestDetailsComponent implements OnInit {
  pageTitle: string = 'Prescription';
  prescriptionId: number;
  prescriptionDetails: IPrescriptionDetails;
  patientPersonalInfo: IPatientPersonalInfo;
  requestInformation: IRequestInformation;
  selectedAddresses: ISelectedAddresses;
  uploadedPrescriptions: string[];
  additionalMedications: string;
  medication: IMedication;
  labTests: ILabTests;
  radiologyTests: ILabTests;
  hospitalProcedures: IMedication;
  modalRef: BsModalRef;
  baseUrl: string = baseUrl;
  prescriptionRequestStatusEnum = PrescriptionRequestStatusEnum;
  prescriptionFulfillmentTypeEnum = PrescriptionFulfillmentTypeEnum;

  constructor(
    private activatedRoute: ActivatedRoute,
    private modalService: BsModalService,
    private prescriptionService: PrescriptionService,
    private datePipe: DatePipe,
    public toastr: ToastrService,
    private filesService: FilesService
  ) { }

  ngOnInit(): void {
    const id = this.activatedRoute.snapshot.params.prescriptionId;

    if (id) {
      this.prescriptionId = id;
      this.getPrescriptionDetails(this.prescriptionId);
    }
  }

  getPrescriptionDetails(prescriptionId) {
    this.prescriptionService
      .getAdminPrescriptionRequestDetails(prescriptionId)
      .subscribe((response) => {
        this.prescriptionDetails = response.data;

        this.patientPersonalInfo =
          this.prescriptionDetails?.patientPersonalInfo;
        this.requestInformation = this.prescriptionDetails?.requestInformation;
        this.selectedAddresses = this.prescriptionDetails?.selectedAddresses;
        this.uploadedPrescriptions =
          this.prescriptionDetails?.uploadedPrescriptions;
        this.additionalMedications =
          this.prescriptionDetails?.additionalMedications;
        this.medication = this.prescriptionDetails?.requestStatus?.medication;
        this.labTests = this.prescriptionDetails?.requestStatus?.labTests;
        this.radiologyTests = this.prescriptionDetails?.requestStatus?.radiologyTests;
        this.hospitalProcedures = this.prescriptionDetails?.requestStatus?.hospitalProcedures;
      });
  }

  handleRequestStatus(status) {
    if (status == this.prescriptionRequestStatusEnum.All) {
      return 'All';
    } else if (status == this.prescriptionRequestStatusEnum.Pending) {
      return 'Pending';
    } else if (status == this.prescriptionRequestStatusEnum.InProgress) {
      return 'InProgress';
    } else if (status == this.prescriptionRequestStatusEnum.Finished) {
      return 'Finished';
    }

    return '--';
  }

  handleDate(dateString) {
    let dateTime = new Date(dateString);

    let date = this.datePipe.transform(dateTime, 'd MMMM, y');
    let time = this.datePipe.transform(dateTime, 'hh:mm a');
    let dateTimeString = date.concat(' at ', time);

    return dateTimeString;
  }

  editMedicationStatus(event, prescriptionType: PrescriptionFulfillmentTypeEnum) {
    event.stopPropagation();

    const initialState: ModalOptions<any> = {
      keyboard: true,
      class: 'modal-dialog-centered',
      initialState: {
        requestId: this.prescriptionId,
        requestStatus: prescriptionType == this.prescriptionFulfillmentTypeEnum.Medicine ?
          this.medication?.requestStatus :
          (prescriptionType == this.prescriptionFulfillmentTypeEnum.Radiology ? this.radiologyTests?.requestStatus
            : this.hospitalProcedures?.requestStatus),
        prescriptionTypeId: prescriptionType
      },
    };

    this.modalRef = this.modalService.show(
      MedicationRequestStatusModalComponent,
      initialState
    );

    this.modalRef.content.event.subscribe((response) => {
      if (prescriptionType == this.prescriptionFulfillmentTypeEnum.Medicine) {
        this.medication['requestStatus'] = response.requestStatus;
        this.medication['processedDate'] = response.data.processedDate;
        if (response.data.closedDate) {
          this.medication['closedDate'] = response.data.closedDate
        }
      }
      else if (prescriptionType == this.prescriptionFulfillmentTypeEnum.Radiology) {
        this.radiologyTests['requestStatus'] = response.requestStatus;
        this.radiologyTests['processedDate'] = response.data.processedDate;
        if (response.data.closedDate) {
          this.radiologyTests['closedDate']= response.data.closedDate;
        }
      }
      else if (prescriptionType == this.prescriptionFulfillmentTypeEnum.HospitalProcedure) {
        this.hospitalProcedures['requestStatus'] = response.requestStatus;
        this.hospitalProcedures['processedDate'] = response.data.processedDate;
        if (response.data.closedDate) {
          this.hospitalProcedures['closedDate']= response.data.closedDate;
        }
      }
    });
  }

  editLabTestsStatus(event) {
    event.stopPropagation();

    const initialState: ModalOptions<any> = {
      keyboard: true,
      class: 'modal-dialog-centered',
      initialState: {
        requestId: this.prescriptionId,
        requestStatus: this.labTests?.requestStatus,
      },
    };

    this.modalRef = this.modalService.show(
      LabTestsStatusModalComponent,
      initialState
    );

    this.modalRef.content.event.subscribe((response) => {
      this.labTests['requestStatus'] = response.requestStatus;
      this.labTests['processedDate'] = response.data.processedDate;
      if (response.data.closedDate) {
        this.labTests['closedDate'] = response.data.closedDate;
      }
    });
  }

  onUploadPrescriptionResult(testResults: IAttachmentsFilesDTO, presciptionType: number) {
    if (this.labTests || this.radiologyTests) {
      const initialState: ModalOptions<any> = {
        keyboard: true,
        class: 'modal-dialog-centered',
        initialState: {
          title: "Upload Test File",
          subtitle: `Please upload tests results below.`,
          filesExisted: testResults.attachmentUrls?.map(f => { return { id: f.id, path: f.path, isEditable: false } })
        },
      };
      this.modalRef = this.modalService.show(
        UploadMultipleFilesModalComponent,
        initialState
      );

      this.modalRef.content.event.subscribe((res) => {
        if (res.action === 'uploadFiles') {
          this.uploadPrescriptionTestResults(this.prescriptionId, res.filesToUpload, presciptionType);
          this.modalRef.hide();
        }
      });
    }
  }

  checkTestLocation(isAtHome) {
    if (isAtHome == null) {
      return '--';
    } else if (isAtHome == true) {
      return 'At Home';
    } else if (isAtHome == false) {
      return 'At Branch';
    }
  }

  handleOpenfile(event, imgSrc) {
    event.stopPropagation();

    if (isPdfFile(imgSrc)) {
      this.filesService.downloadFile(imgSrc);
      return;
    }

    const initialState: ModalOptions<any> = {
      keyboard: true,
      class: 'modal-dialog-centered',
      initialState: {
        src: imgSrc,
      },
    };

    this.modalRef = this.modalService.show(ImageModalComponent, initialState);
  }

  uploadPrescriptionTestResults(ehrTransactionId: number, files, presciptionType: number) {
    this.prescriptionService.uploadTestResults(ehrTransactionId, files)
      .subscribe((response) => {
        if (response?.data) {
          let currentDateTime = new Date();
          let utcCurrentDateTime = new Date(currentDateTime.getUTCFullYear(),
            currentDateTime.getUTCMonth(),
            currentDateTime.getUTCDate(),
            currentDateTime.getUTCHours() + 2,
            currentDateTime.getUTCMinutes(),
            currentDateTime.getUTCSeconds()
          );
          if (presciptionType == this.prescriptionFulfillmentTypeEnum.LabTest) {
            this.labTests.attachmentUrls = response.data.attachmentUrls;
            this.labTests.closedDate = this.labTests.closedDate || utcCurrentDateTime;
            this.labTests.requestStatus = this.prescriptionRequestStatusEnum.Finished;
          }
          else if (presciptionType == this.prescriptionFulfillmentTypeEnum.Radiology) {
            this.radiologyTests.attachmentUrls = response.data.attachmentUrls;
            this.radiologyTests.closedDate = this.radiologyTests.closedDate || utcCurrentDateTime;
            this.radiologyTests.requestStatus = this.prescriptionRequestStatusEnum.Finished;
          }
          showNotification(
            'success',
            'The results have been uploaded successfully.',
            this.toastr
          );
        }
        else {
          showNotification(
            'danger',
            `${response?.errorList[0]?.message}`,
            this.toastr
          )
        }
      });
  }
}
